import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ShieldCheck, Lock, FileCheck2, Server, Bug, Download } from 'lucide-react';
import SEO from '../components/SEO';
import TrustSubNav from '../components/TrustSubNav';
import { CompaniesOfficeBlock, CompaniesOfficeStrip } from '../components/CompaniesOfficeBlock';
import { TrustChip, TRUST_STATES } from '../components/TrustChip';

// =============================================================================
// GIWAHS — Trust Centre (review-only)
// Source of truth: /app/memory/founder_vault/37_GIWAHS_Trust_Centre_Page.md
// Documents marked "Pending publication" are not downloadable yet.
// =============================================================================

const TRUST_STATUS = [
  { key: 'platform',  label: 'Platform status',                       value: 'All systems operational',           href: '/status' },
  { key: 'review',    label: 'Last security review',                  value: 'Continuous — merge-time review' },
  { key: 'pentest',   label: 'Next third-party penetration test',     value: 'Scheduled Q3 2026' },
  { key: 'incidents', label: 'Customer-impacting incidents (12 mo.)', value: '0',                                 href: '/status' },
];

const DOC_LIBRARY = [
  { slug: 'privacy',       name: 'Privacy Policy',                       category: 'Privacy' },
  { slug: 'terms',         name: 'Terms of Service',                     category: 'Legal' },
  { slug: 'fp-terms',      name: 'Founding Partner Terms',               category: 'Membership' },
  { slug: 'refund',        name: 'Refund and Cancellation Policy',       category: 'Legal' },
  { slug: 'cookies',       name: 'Cookie Policy',                        category: 'Privacy' },
  { slug: 'dpa',           name: 'Data Processing Agreement (DPA)',      category: 'Data protection' },
  { slug: 'security',      name: 'Security & Incident Response Policy',  category: 'Security' },
  { slug: 'subprocessors', name: 'Subprocessor Register',                category: 'Data protection' },
];

const TrustCentre = () => {
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Trust Centre', url: '/trust' },
  ];

  return (
    <div data-testid="trust-centre-page" className="surface-obsidian text-platinum">
      <SEO
        title="Trust Centre — Security, Privacy, Compliance"
        description="GIWAHS Trust Centre: the receipts behind every promise on this site. Security posture, privacy commitments, compliance frameworks, Subprocessor list, incident response, and downloadable policy documents."
        breadcrumbs={breadcrumbs}
      />

      {/* Hero */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ Trust Centre</span>
          <h1
            className="mt-6 max-w-4xl font-display tracking-tighter"
            style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 7vw, 5.5rem)', lineHeight: 0.95 }}
          >
            The receipts<br />behind every promise<br />on this site.
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-neutral-700 md:text-lg">
            GIWAHS is built on earned trust — through transparency, verification, and consistent
            behaviour over time. This page exists so you can verify ours.
            Security, privacy, compliance — each anchored to a contractual document.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 text-xs uppercase tracking-wider text-neutral-500 font-mono">
            <span className="inline-flex items-center gap-1.5">
              <Lock size={12} strokeWidth={1.5} /> TLS 1.2+ · AES-256
            </span>
            <span>·</span>
            <span>Stripe-secured payments</span>
            <span>·</span>
            <span>GDPR · UK GDPR · CCPA · NZ Privacy Act · AU Privacy Act</span>
            <span>·</span>
            <span>72-hour breach notification</span>
          </div>
        </div>
      </section>

      <TrustSubNav />

      {/* Live status board */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ 01 — Status board</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter" style={{ fontWeight: 600 }}>
            Live trust indicators.
          </h2>
          <div className="mt-8 grid gap-px bg-black sm:grid-cols-2 lg:grid-cols-4">
            {TRUST_STATUS.map((s) => {
              const inner = (
                <>
                  <div className="label-overline tracking-wider">{s.label}</div>
                  <div className="mt-4 font-display text-xl tracking-tight" style={{ fontWeight: 600 }}>
                    {s.value}
                  </div>
                </>
              );
              return s.href ? (
                <Link
                  key={s.key}
                  to={s.href}
                  data-testid={`trust-status-${s.key}`}
                  className="bg-white p-6 transition hover:bg-neutral-50"
                >
                  {inner}
                  <div className="mt-3 inline-flex items-center gap-1 text-xs uppercase tracking-wider text-neutral-500 font-mono">
                    View status page <ArrowUpRight size={12} strokeWidth={1.5} />
                  </div>
                </Link>
              ) : (
                <div
                  key={s.key}
                  data-testid={`trust-status-${s.key}`}
                  className="bg-white p-6"
                >
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* D-068 · Trust marks legend */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-20" data-testid="trust-marks-legend">
          <span className="label-overline">/ 01a — Trust marks</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            What each mark means.
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-neutral-700">
            Every chip you see on the platform — beside a factory, supplier, RFQ response, or founder — is one of five marks. They are awarded only with evidence, and every claim links back to this Trust Centre.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {[
              { state: 'verified',         desc: 'Entity exists, basic checks passed.' },
              { state: 'trusted',          desc: 'Repeat orders, audit history, no incidents.' },
              { state: 'elite-verified',   desc: 'Full audit · certifications · insurance verified.' },
              { state: 'founding-partner', desc: 'Founding Partner of GIWAHS · 10-year price-lock.' },
              { state: 'under-review',     desc: 'Verification in process · evidence pending.' },
            ].map((m) => (
              <div key={m.state} className="border border-black bg-white p-5 flex flex-col gap-3">
                <TrustChip state={m.state} variant="solid" size="sm" />
                <p className="text-sm leading-relaxed text-neutral-700">{m.desc}</p>
              </div>
            ))}
          </div>
          {/* Founder mark — public representation */}
          <div className="mt-8 border border-black bg-white p-5">
            <div className="label-overline text-neutral-500">Founder mark</div>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <TrustChip state="founding-partner" variant="solid" size="sm" />
              <span className="font-mono-data text-[11px] uppercase tracking-[0.14em] text-neutral-500">Gold · Elite · Enterprise · Founding Partner programme</span>
            </div>
          </div>
          <div className="mt-6 font-mono-data text-[11px] uppercase tracking-[0.14em] text-neutral-500">
            All five states: {TRUST_STATES.join(' · ')}
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 02 — Three pillars</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Read the section that matters to you.
          </h2>
          <div className="mt-12 grid gap-px bg-black md:grid-cols-3">
            {[
              {
                to: '/trust/security',
                testid: 'trust-pillar-security',
                icon: ShieldCheck,
                title: 'Security',
                bullets: [
                  'TLS 1.2+ in transit · AES-256 at rest',
                  'MFA + SSO + RBAC on production systems',
                  'Daily encrypted backups, quarterly access reviews',
                ],
              },
              {
                to: '/trust/privacy',
                testid: 'trust-pillar-privacy',
                icon: FileCheck2,
                title: 'Privacy',
                bullets: [
                  'GDPR · UK GDPR · CCPA · NZ Privacy Act · AU Privacy Act aligned',
                  'No sale or share of personal data — ever',
                  'Self-service export and deletion',
                ],
              },
              {
                to: '/trust/compliance',
                testid: 'trust-pillar-compliance',
                icon: Server,
                title: 'Compliance',
                bullets: [
                  'EU SCCs Modules 2/3 · UK IDTA · Swiss addendum',
                  'Pre-signed DPA available on request',
                  'Subprocessor list public · 30-day change notification',
                ],
              },
            ].map((p) => (
              <Link
                key={p.to}
                to={p.to}
                data-testid={p.testid}
                className="group flex flex-col bg-white p-7 transition hover:bg-neutral-50"
              >
                <p.icon size={24} strokeWidth={1.5} />
                <div
                  className="mt-6 font-display text-2xl tracking-tight"
                  style={{ fontWeight: 600 }}
                >
                  {p.title}
                </div>
                <ul className="mt-4 flex-1 space-y-2 text-sm leading-relaxed text-neutral-700">
                  {p.bullets.map((b) => <li key={b}>· {b}</li>)}
                </ul>
                <div className="mt-6 inline-flex items-center gap-2 label-overline group-hover:gap-3 transition-all">
                  Read more <ArrowUpRight size={14} strokeWidth={1.5} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Document library */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-16 md:px-8 md:py-24">
          <span className="label-overline">/ 03 — Document library</span>
          <h2 className="mt-3 font-display text-3xl tracking-tighter md:text-4xl" style={{ fontWeight: 600 }}>
            Every policy, downloadable.
          </h2>
          <p className="mt-4 max-w-2xl text-sm text-neutral-600">
            The canonical library of GIWAHS customer-assurance documentation. All policies are
            versioned and append-only, and material changes are notified to existing customers
            under each document's change clause.
          </p>

          <div className="mt-10 overflow-x-auto">
            <table className="w-full min-w-[640px] border-collapse border border-black bg-white text-sm">
              <thead className="bg-neutral-100">
                <tr>
                  <th className="border border-black p-4 text-left label-overline">Document</th>
                  <th className="border border-black p-4 text-left label-overline">Category</th>
                  <th className="border border-black p-4 text-left label-overline">Access</th>
                </tr>
              </thead>
              <tbody>
                {DOC_LIBRARY.map((d) => (
                  <tr key={d.slug} data-testid={`trust-doc-${d.slug}`}>
                    <td className="border border-black p-4 font-medium">{d.name}</td>
                    <td className="border border-black p-4 text-neutral-600 font-mono text-xs">{d.category}</td>
                    <td className="border border-black p-4">
                      <a
                        href={`/legal/${d.slug}.html`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 underline hover:no-underline"
                      >
                        <Download size={14} strokeWidth={1.5} /> View document
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-neutral-500 font-mono">
            All documents open in a new tab and print to PDF (Cmd/Ctrl-P). The full index is at{' '}
            <a href="/legal/index.html" target="_blank" rel="noopener noreferrer" className="underline">the document index</a>.
          </p>
        </div>
      </section>

      {/* Data-subject + responsible disclosure + contact */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-2 md:px-8 md:py-24">
          <div>
            <span className="label-overline">/ 04 — Your rights</span>
            <h3 className="mt-3 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>
              Exercise your data rights in three clicks.
            </h3>
            <div className="mt-6 space-y-3">
              <a
                data-testid="trust-data-request"
                href="mailto:privacy@giwahs.com?subject=Data%20Access%20Request"
                className="block border border-neutral-900 bg-white p-4 transition hover:bg-neutral-50"
              >
                <div className="font-medium">Request your data</div>
                <div className="mt-1 text-sm text-neutral-600">Get a copy of everything we hold about you.</div>
              </a>
              <a
                data-testid="trust-data-delete"
                href="mailto:privacy@giwahs.com?subject=Data%20Deletion%20Request"
                className="block border border-neutral-900 bg-white p-4 transition hover:bg-neutral-50"
              >
                <div className="font-medium">Delete your account / data</div>
                <div className="mt-1 text-sm text-neutral-600">Subject to legal retention exceptions.</div>
              </a>
              <a
                data-testid="trust-complaint"
                href="mailto:privacy@giwahs.com?subject=Privacy%20Complaint"
                className="block border border-neutral-900 bg-white p-4 transition hover:bg-neutral-50"
              >
                <div className="font-medium">Lodge a complaint</div>
                <div className="mt-1 text-sm text-neutral-600">
                  privacy@giwahs.com first; escalate to your local supervisory authority if unresolved.
                </div>
              </a>
            </div>
          </div>

          <div>
            <span className="label-overline">/ 05 — Responsible disclosure</span>
            <h3 className="mt-3 font-display text-2xl tracking-tight" style={{ fontWeight: 600 }}>
              Found a vulnerability?
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-neutral-700">
              We welcome good-faith security research and offer safe-harbour for researchers
              acting under our policy. Report findings to security@giwahs.com — acknowledgement
              within 2 business days, triage within 5, remediation per severity.
            </p>
            <a
              data-testid="trust-disclosure-link"
              href="mailto:security@giwahs.com"
              className="mt-6 inline-flex items-center gap-2 border-b border-neutral-900 pb-1 text-sm font-medium hover:gap-3 transition-all"
            >
              <Bug size={14} strokeWidth={1.5} /> Email security@giwahs.com
              <ArrowUpRight size={14} strokeWidth={1.5} />
            </a>

            <div className="mt-10 border-t border-neutral-200 pt-6">
              <span className="label-overline text-neutral-500">/ Contact paths</span>
              <ul className="mt-4 space-y-2 text-sm">
                <li data-testid="trust-contact-privacy">
                  Privacy & data subject rights · <a href="mailto:privacy@giwahs.com" className="underline">privacy@giwahs.com</a>
                </li>
                <li data-testid="trust-contact-security">
                  Security incidents · <a href="mailto:security@giwahs.com" className="underline">security@giwahs.com</a>
                </li>
                <li data-testid="trust-contact-legal">
                  Legal & contracts · <a href="mailto:legal@giwahs.com" className="underline">legal@giwahs.com</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Companies Office verification (D-057) */}
      <CompaniesOfficeBlock />
      <CompaniesOfficeStrip />
    </div>
  );
};

export default TrustCentre;
